import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Award,
  BookOpenCheck,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  FilePenLine,
  Loader2,
  MessageSquareText,
  Search,
  UserRound,
  X,
} from "lucide-react";
import { fmtDateTime, norm } from "@/components/RelationSelects";
import { EmptyState, PageHeader, PageLoading, SectionCard, StatCard } from "@/components/doxa/AdminSurface";

const inputCls = "w-full min-h-10 px-3 py-2 bg-white border border-[#E0D9CD] rounded-xl text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#AAA198] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

function PendingChip({ count }) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#F3D9A7] bg-[#FFF7E8] px-2.5 text-[11px] font-semibold text-[#9A6518]">
      <FilePenLine className="h-3.5 w-3.5" aria-hidden="true" />
      {count} {count === 1 ? "pendente" : "pendentes"}
    </span>
  );
}

function ModalShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-3 sm:p-4" onClick={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-[24px] border border-[#E0D9CD] bg-[#FCFAF7] shadow-[0_24px_80px_rgba(45,31,20,.22)]"
        onClick={(event) => event.stopPropagation()}
        data-testid="grading-modal"
      >
        {children}
      </div>
    </div>
  );
}

export default function Grading() {
  const [items, setItems] = useState([]);
  const [grading, setGrading] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/evaluations/grading/pending");
      setItems(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const pendingEssays = useMemo(
    () => items.reduce((total, item) => total + (Number(item.pending_essays) || 0), 0),
    [items],
  );

  const filtered = useMemo(() => {
    const normalized = norm(query);
    if (!normalized) return items;
    return items.filter((item) => norm([item.evaluation_title, item.student_name].join(" ")).includes(normalized));
  }, [items, query]);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="grading-page">
      <PageHeader
        compact
        eyebrow="Acadêmico"
        title="Correções"
        description="Revise respostas dissertativas com contexto claro e finalize a pontuação de cada aluno."
        meta={`${items.length} ${items.length === 1 ? "tentativa" : "tentativas"} na fila de correção`}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          compact
          title="Tentativas na fila"
          value={items.length}
          hint="Submissões com pelo menos uma dissertativa pendente."
          icon={ClipboardCheck}
          tone="orange"
          testId="grading-stat-attempts"
        />
        <StatCard
          compact
          title="Questões pendentes"
          value={pendingEssays}
          hint="Total de respostas dissertativas que ainda precisam de pontuação."
          icon={BookOpenCheck}
          tone="warning"
          testId="grading-stat-essays"
        />
      </div>

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-[#1F1F1F]">Fila de correção</p>
            <p className="mt-0.5 text-xs text-[#6B655E]">Avaliações e alunos que ainda exigem revisão manual.</p>
          </div>
          <div className="relative w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A59C90]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por avaliação ou aluno…"
              className="h-11 w-full rounded-xl border border-[#E0D9CD] bg-white pl-9 pr-9 text-sm outline-none transition placeholder:text-[#AAA198] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
              data-testid="grading-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-[#8A8378] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]"
                aria-label="Limpar busca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {loading ? (
        <PageLoading label="Carregando fila de correção…" />
      ) : filtered.length === 0 ? (
        <section className="rounded-2xl border border-[#E0D9CD] bg-white">
          <EmptyState
            icon={CheckCircle2}
            title={items.length === 0 ? "Nenhuma correção pendente" : "Nenhum resultado encontrado"}
            description={items.length === 0 ? "Quando houver respostas dissertativas para revisar, elas aparecerão nesta fila." : "Tente limpar a busca ou usar outro termo."}
          />
          {items.length === 0 && <span className="sr-only" data-testid="grading-empty">Nenhuma correção pendente.</span>}
        </section>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <article
              key={item.attempt_id}
              className="group relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white transition hover:border-[#D6C9BA] hover:shadow-[0_10px_28px_rgba(68,53,35,0.05)]"
              data-testid={`grading-row-${item.attempt_id}`}
            >
              <div className="absolute inset-y-0 left-0 w-1 bg-[#F75A1B] opacity-70" />
              <div className="flex flex-col gap-4 p-4 pl-5 sm:p-5 sm:pl-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <PendingChip count={item.pending_essays} />
                    {item.submitted_at && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#8A8378]">
                        <Clock3 className="h-3.5 w-3.5" aria-hidden="true" /> Enviada {fmtDateTime(item.submitted_at)}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-3 break-words font-display text-lg font-bold leading-snug text-[#111111] sm:text-xl">{item.evaluation_title}</h2>
                  <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-[#6B655E]">
                    <UserRound className="h-4 w-4 shrink-0 text-[#A59C90]" aria-hidden="true" />
                    <span className="break-words">{item.student_name}</span>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setGrading(item.attempt_id)}
                  className="doxa-btn-primary inline-flex h-10 shrink-0 items-center justify-center gap-2 px-4 text-sm"
                  data-testid={`grade-attempt-${item.attempt_id}`}
                >
                  <ClipboardCheck className="h-4 w-4" /> Corrigir
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {grading && (
        <GradingModal
          attemptId={grading}
          onClose={() => {
            setGrading(null);
            void load({ silent: true });
          }}
        />
      )}
    </div>
  );
}

function GradingModal({ attemptId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: response } = await api.get(`/evaluation-attempts/${attemptId}/grading`);
      setData(response);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [attemptId]);

  if (loading && !data) {
    return (
      <ModalShell onClose={onClose}>
        <div className="flex min-h-72 items-center justify-center p-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E0D9CD] bg-white px-4 py-2 text-sm text-[#6B655E]">
            <Loader2 className="h-4 w-4 animate-spin text-[#F75A1B]" /> Carregando correção…
          </div>
        </div>
      </ModalShell>
    );
  }

  if (!data) return null;

  const essays = data.essays || [];
  const gradedCount = essays.filter((essay) => essay.points_awarded != null).length;
  const pendingCount = essays.length - gradedCount;

  return (
    <ModalShell onClose={onClose}>
      <header className="sticky top-0 z-10 border-b border-[#EBE5DB] bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Mesa de correção</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h2 className="break-words font-display text-xl font-bold sm:text-2xl">{data.evaluation_title}</h2>
              {pendingCount > 0 ? (
                <PendingChip count={pendingCount} />
              ) : (
                <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#CFE8D8] bg-[#EEF8F2] px-2.5 text-[11px] font-semibold text-[#2F7A45]">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Tudo corrigido
                </span>
              )}
            </div>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#6B655E]">
              <UserRound className="h-4 w-4" aria-hidden="true" /> {data.student_name}
            </p>
            <p className="mt-1 text-xs text-[#8A8378]">{gradedCount}/{essays.length} dissertativa(s) corrigida(s)</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="space-y-4 p-5 sm:p-6">
        <SectionCard
          compact
          icon={ClipboardCheck}
          title="Respostas dissertativas"
          description="Leia a resposta do aluno, atribua a pontuação e registre um comentário quando necessário."
        >
          <div className="space-y-4">
            {essays.map((essay, index) => (
              <EssayGrader
                key={essay.answer_id || essay.question_id}
                essay={essay}
                index={index}
                onGraded={load}
              />
            ))}
            {essays.length === 0 && (
              <EmptyState
                compact
                icon={CheckCircle2}
                title="Sem questões dissertativas"
                description="Não há respostas dissertativas disponíveis para esta tentativa."
              />
            )}
          </div>
        </SectionCard>
      </div>
    </ModalShell>
  );
}

function EssayGrader({ essay, index, onGraded }) {
  const [pts, setPts] = useState(essay.points_awarded ?? "");
  const [comment, setComment] = useState(essay.grader_comment || "");
  const [busy, setBusy] = useState(false);
  const graded = essay.points_awarded != null;

  useEffect(() => {
    setPts(essay.points_awarded ?? "");
    setComment(essay.grader_comment || "");
  }, [essay.points_awarded, essay.grader_comment]);

  const save = async () => {
    if (pts === "" || Number.isNaN(Number(pts))) return toast.error("Informe a pontuação.");
    if (!essay.answer_id) return toast.error("O aluno não respondeu esta questão.");
    setBusy(true);
    try {
      await api.post(`/evaluation-answers/${essay.answer_id}/grade`, {
        points_awarded: Number(pts),
        grader_comment: comment,
      });
      toast.success("Correção salva");
      await onGraded();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white"
      data-testid={`essay-grader-${essay.question_id}`}
    >
      <div className="border-b border-[#EBE5DB] bg-[#FFFCF8] p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#FFF0E9] text-xs font-bold text-[#D94A12]">{index + 1}</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8A8378]">Dissertativa</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#6B655E]">
              <Award className="h-3.5 w-3.5 text-[#A59C90]" /> vale {essay.points} ponto(s)
            </span>
          </div>
          {graded && (
            <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#CFE8D8] bg-[#EEF8F2] px-2.5 text-[11px] font-semibold text-[#2F7A45]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Corrigida
            </span>
          )}
        </div>
        <p className="mt-3 break-words text-sm font-semibold leading-relaxed text-[#1F1F1F] sm:text-base">{essay.prompt}</p>
      </div>

      <div className="space-y-4 p-4 sm:p-5">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#6B655E]">
            <BookOpenCheck className="h-4 w-4 text-[#F75A1B]" /> Resposta do aluno
          </div>
          <div
            className="min-h-20 whitespace-pre-wrap rounded-xl border border-[#EBE5DB] bg-[#FAF7F1] p-3 text-sm leading-relaxed text-[#2F2A25]"
            data-testid={`essay-answer-${essay.question_id}`}
          >
            {essay.essay_answer || <span className="italic text-[#8A8378]">Sem resposta.</span>}
          </div>
        </div>

        <div className="rounded-xl border border-[#EBE5DB] bg-[#FFFDFC] p-3 sm:p-4">
          <div className="mb-3 flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-[#F75A1B]" />
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8A8378]">Sua correção</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-[150px_minmax(0,1fr)]">
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6B655E]">Pontos (0–{essay.points})</label>
              <input
                type="number"
                min="0"
                max={essay.points}
                step="0.5"
                value={pts}
                onChange={(event) => setPts(event.target.value)}
                className={inputCls}
                data-testid={`essay-points-${essay.question_id}`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[#6B655E]">Comentário para a correção <span className="font-normal">(opcional)</span></label>
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={2}
                className={`${inputCls} resize-y`}
                placeholder="Registre uma orientação ou observação sobre a resposta…"
                data-testid={`essay-comment-${essay.question_id}`}
              />
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="doxa-btn-primary inline-flex min-h-10 items-center justify-center gap-2 px-4 text-sm disabled:opacity-60"
              data-testid={`essay-save-${essay.question_id}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {busy ? "Salvando…" : "Salvar correção"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
